import { useEffect } from "react";
import styled from "styled-components";

import { GuideSection } from "../CompletionStatus";
import { CompletionTable } from "../components/CompletionTable";
import { ProgressBar } from "../components/ProgressBar";
import { SendApplicationButton } from "../components/SendApplicationButton";

import { VerticalSpacing, Text, Card, Button } from "@otta/design";
import { useQuery } from "@otta/search/apollo";
import { Loading } from "@otta/search/components/Loading";
import { pushAnalyticsEvent } from "@otta/analytics";
import { PublicJobDocument } from "@otta/search/schema";
import { Link } from "@otta/search/components/Link";
import { useUserProfileStatus } from "@otta/search/utils/user";
import { useCheckApplicationProgress } from "@otta/search/utils/job";

const PreviewLink = styled(Link)`
  display: block;
  button {
    width: 100%;
  }
`;
interface IApplicationStatusProps {
  jobId: string;
  isLive: boolean;
  showConfirmationPopup: () => void;
}

export function ApplicationStatus({
  jobId,
  isLive,
  showConfirmationPopup,
}: IApplicationStatusProps): React.ReactElement {
  const { data: jobData } = useQuery(PublicJobDocument, {
    variables: { externalId: jobId },
  });

  const { profileState, completedFields } = useUserProfileStatus();

  const job = jobData?.publicJob;

  const { numberCompleted, rows } = useCheckApplicationProgress(jobId);

  useEffect(() => {
    if (profileState !== "NOT_CREATED") {
      pushAnalyticsEvent({
        eventName: "Candidate Completed Profile",
        completedFields,
        profileState,
      });
    }
    if (profileState === "DEEP" || profileState === "FULL") {
      pushAnalyticsEvent({
        eventName: "Candidate Completed Deep Profile",
        completedFields,
        profileState,
      });
    }
  }, [profileState, completedFields]);

  if (!job) {
    return <Loading />;
  }

  return (
    <VerticalSpacing>
      <Card>
        <VerticalSpacing size={0}>
          <Text bold>
            Applying for{" "}
            <Link to={`/jobs/${jobId}`} targetBlank>
              {job.title}
            </Link>{" "}
            at {job.company.name}
          </Text>
          <ProgressBar
            numberCompleted={numberCompleted}
            numberOfBars={rows.length}
          />
          <CompletionTable rows={rows} />
          {!isLive && <Text bold>This job is no longer available</Text>}
          <PreviewLink underline={false} to={`/jobs/${jobId}/apply/preview`}>
            <Button
              disabled={!isLive || numberCompleted < rows.length}
              level="primary"
            >
              Preview application
            </Button>
          </PreviewLink>
          <SendApplicationButton
            jobId={jobId}
            disabled={!isLive || numberCompleted < rows.length}
            showConfirmationPopup={showConfirmationPopup}
            level="secondary"
          />
        </VerticalSpacing>
      </Card>
      <GuideSection />
    </VerticalSpacing>
  );
}
